<script setup lang="ts">
import { onKeyStroke } from "@vueuse/core";
import { isAxiosError } from "axios";
import type { SerializedEditorState } from "lexical";
import { computed, nextTick, onBeforeUnmount, ref } from "vue";
import { AVMedia } from "vue-audio-visual";

import { backendOld } from "~/api";
import AttachmentInputButton from "~/components/dumb/AttachmentInputButton.vue";
import Button from "~/components/dumb/Button.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import {
  ensureRecorderFormat,
  notifyGenericFailedRecording,
  notifyGenericTranscriptionFailed,
  notifyNotAllowedRecordingError,
  notifyNotFoundRecordingError,
  notifyShortRecordingWarning,
  notifyUnsupportedRecording,
  SHORT_RECORDING_ERROR_MESSAGE,
} from "~/components/speechToText/shared";
import TextHistoryModal from "~/components/text/TextHistoryModal.vue";
import { getCopyItems } from "~/components/text/copyItems";
import { colorsByTheme } from "~/constants/style";
import { CheckIcon, DotsHorizontalIcon, HistoryFieldIcon, MicrophoneIcon, XIcon } from "~/icons";
import {
  ButtonSize,
  ButtonStyle,
  CommandId,
  DropdownMenuItemKind,
  EditorMode,
  IconSize,
  Placement,
} from "~/shared/enums";
import type { DropdownMenuSection, SpeechToTextFormat, TextSnapshotSource } from "~/shared/types";
import { useAppStore, usePageStore, useUserStore } from "~/stores";
import { makeDuid } from "~/utils/common";
import { printOrCaptureException } from "~/utils/exceptionClient";

const props = defineProps<{
  showText?: boolean;
  showAttachment?: boolean;
  textHistorySource?: TextSnapshotSource;
  isContrast?: boolean;
  getContent?: () => SerializedEditorState | null | undefined;
  sections?: DropdownMenuSection[];
  widthPixels?: number;
}>();

const emit = defineEmits<{
  focus: [];
  result: [text: string];
  attachment: [files: File[]];
  restore: [state: SerializedEditorState];
}>();

const RECORDING_WARNING_MESSAGE =
  "A recording is currently in progress. If you continue, it will be discarded. Do you want to proceed?";

const appStore = useAppStore();
const pageStore = usePageStore();
const userStore = useUserStore();

const hasAccessToImportExport = computed(() => userStore.hasAccessToImportExport());

const stream = ref<MediaStream | null>(null);
const recorder = ref<MediaRecorder | null>(null);
const recordingStartedAt = ref<number | null>(null);
const recorderConfig = ref<SpeechToTextFormat | null>(null);

const controlsDuid = ref<string>(makeDuid());

const isStarting = ref(false);
const isRecording = ref(false);
const isTranscribing = ref(false);
const isHistoryModalOpen = ref(false);
const working = computed(() => isStarting.value || isRecording.value || isTranscribing.value);

const activeRunDuid = ref<string | null>(null);
const pendingStartDuid = ref<string | null>(null);

const visualizerLineColor = computed(() => colorsByTheme[pageStore.theme].textVlt);

const menuSections = computed<DropdownMenuSection[]>(() => [
  ...(props.textHistorySource
    ? [
        {
          title: "History",
          items: [
            {
              title: "History",
              kind: DropdownMenuItemKind.BUTTON,
              icon: HistoryFieldIcon,
              onClick: () => {
                isHistoryModalOpen.value = true;
              },
            },
          ],
        },
      ]
    : []),
  ...(props.sections ?? []),
  ...(props.getContent
    ? [
        {
          title: "Copy",
          items: getCopyItems("Text", props.getContent?.()),
        },
      ]
    : []),
]);

// oxlint-disable-next-line no-alert
const displayWarning = () => window.confirm(RECORDING_WARNING_MESSAGE);

const stopTracks = () => {
  const currentStream = stream.value;
  const tracks = currentStream?.getTracks?.() ?? [];
  tracks.forEach((t) => {
    t.stop();
  });
  stream.value = null;
};

const reset = () => {
  isStarting.value = false;
  isRecording.value = false;
  pendingStartDuid.value = null;
  recorder.value = null;
  recordingStartedAt.value = null;
  stopTracks();
  appStore.setIsRecording(controlsDuid.value, false);
};

const transcribe = async (blob: Blob, runDuid: string) => {
  try {
    const config = recorderConfig.value;
    if (!config) {
      return;
    }

    const formData = new FormData();
    const file = new File([blob], `audio.${config.extension}`, { type: config.uploadMimeType });
    formData.append("audio", file);
    formData.append("audio_extension", config.extension);

    const { data } = await backendOld.speechToText.transcribe(formData);

    if (activeRunDuid.value !== runDuid) {
      return;
    }

    emit("result", data.transcript ?? "");
  } catch (error) {
    if (
      isAxiosError<{ errors?: string[] }>(error) &&
      error.response?.status === 400 &&
      error.response.data?.errors?.[0] === SHORT_RECORDING_ERROR_MESSAGE
    ) {
      notifyShortRecordingWarning();
      return;
    }
    notifyGenericTranscriptionFailed();
    printOrCaptureException(error);
  } finally {
    nextTick(() => {
      if (activeRunDuid.value !== runDuid) {
        return;
      }
      isTranscribing.value = false;
      activeRunDuid.value = null;
      reset();
    });
  }
};

const handleRecorderStop = (chunks: BlobPart[]) => {
  if (!recorderConfig.value) {
    return;
  }

  const blob = new Blob(chunks, { type: recorderConfig.value.uploadMimeType });
  stopTracks();

  const startedAt = recordingStartedAt.value;
  recordingStartedAt.value = null;

  const runDuid = activeRunDuid.value;

  if (!runDuid) {
    reset();
    return;
  }

  const durationMs = startedAt ? performance.now() - startedAt : 0;

  if (durationMs < 1000) {
    activeRunDuid.value = null;
    isTranscribing.value = false;
    reset();
    notifyShortRecordingWarning();
    return;
  }

  transcribe(blob, runDuid);
};

const createRecorder = (mediaStream: MediaStream, options: MediaRecorderOptions) => {
  const chunks: BlobPart[] = [];
  const rec = new MediaRecorder(mediaStream, options);

  rec.ondataavailable = (event: BlobEvent) => {
    if (event.data?.size) {
      chunks.push(event.data);
    }
  };

  rec.onstop = () => handleRecorderStop(chunks);

  return rec;
};

const startRecording = async () => {
  if (working.value) {
    return;
  }

  const format = recorderConfig.value ?? ensureRecorderFormat();
  if (!format) {
    notifyUnsupportedRecording();
    return;
  }

  recorderConfig.value = format;
  emit("focus");
  const startDuid = makeDuid();
  pendingStartDuid.value = startDuid;
  isStarting.value = true;
  appStore.setIsRecording(controlsDuid.value, true);

  try {
    const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    if (pendingStartDuid.value !== startDuid) {
      mediaStream.getTracks().forEach((track) => track.stop());
      return;
    }

    stream.value = mediaStream;
    recorder.value = createRecorder(mediaStream, { mimeType: format.recorderMimeType });
    recorder.value.start();
    recordingStartedAt.value = performance.now();
    isRecording.value = true;
  } catch (error) {
    if (pendingStartDuid.value !== startDuid) {
      return;
    }
    reset();

    if (error instanceof Error && error.name === "NotFoundError") {
      notifyNotFoundRecordingError();
      return;
    }

    if (error instanceof Error && error.name === "NotAllowedError") {
      notifyNotAllowedRecordingError();
      return;
    }

    notifyGenericFailedRecording();
    printOrCaptureException(error);
  } finally {
    if (pendingStartDuid.value === startDuid) {
      pendingStartDuid.value = null;
      isStarting.value = false;
    }
  }
};

const cancelRecording = () => {
  if (!working.value) {
    return;
  }
  const wasStarting = isStarting.value;
  pendingStartDuid.value = null;
  isStarting.value = false;
  activeRunDuid.value = null;
  isTranscribing.value = false;
  isRecording.value = false;

  appStore.setIsRecording(controlsDuid.value, false);

  if (wasStarting) {
    reset();
    return;
  }

  if (recorder.value?.state === "inactive") {
    reset();
    return;
  }
  recorder.value?.stop?.();
};

const finishRecording = () => {
  if (!isRecording.value) {
    return;
  }
  emit("focus");
  const runDuid = makeDuid();
  activeRunDuid.value = runDuid;

  isTranscribing.value = true;
  isRecording.value = false;
  recorder.value?.stop?.();
};

const confirmClearActiveRecording = () => {
  if (!working.value) {
    return true;
  }
  const shouldClear = displayWarning();
  if (!shouldClear) {
    return false;
  }
  cancelRecording();
  return true;
};

onKeyStroke("Enter", (event) => {
  if (!working.value) {
    return;
  }
  event.preventDefault();
  finishRecording();
});

onKeyStroke("Escape", (event) => {
  if (!working.value) {
    return;
  }
  event.preventDefault();
  cancelRecording();
});

onBeforeUnmount(reset);

defineExpose({
  working,
  confirmClearActiveRecording,
  reset,
  startRecording,
  finishRecording,
  cancelRecording,
});
</script>

<template>
  <TextHistoryModal
    v-if="isHistoryModalOpen && textHistorySource"
    :source="textHistorySource"
    @close="isHistoryModalOpen = false"
    @restore="(state: SerializedEditorState) => emit('restore', state)" />
  <div v-if="!pageStore.isPublic" class="flex h-[26px] items-center gap-1">
    <Tooltip v-if="!working" :command-id="CommandId.EDITOR_START_DICTATION">
      <Button
        :btn-style="ButtonStyle.SECONDARY"
        :icon="MicrophoneIcon"
        :icon-size="IconSize.S"
        :icon-args="{ class: 'text-vlt' }"
        :size="ButtonSize.CHIP"
        :text="showText ? 'Dictate' : undefined"
        text-style="text-vlt"
        :class="showText && '!px-1 !py-px'"
        a11y-label="Dictate text"
        borderless
        :is-contrast="isContrast"
        @click="startRecording" />
    </Tooltip>
    <Tooltip v-if="showAttachment && hasAccessToImportExport && !working" text="Add attachment">
      <AttachmentInputButton
        :editor-mode="EditorMode.DOC"
        :is-contrast="isContrast"
        @input="(files) => emit('attachment', files)" />
    </Tooltip>
    <DropdownMenu
      v-if="menuSections.length > 0 && !working"
      :sections="menuSections"
      :placement="Placement.BOTTOM_RIGHT"
      :width-pixels="widthPixels ?? 192"
      :distance="2"
      @click.prevent>
      <Tooltip text="More options">
        <Button
          :btn-style="ButtonStyle.SECONDARY"
          :icon="DotsHorizontalIcon"
          :icon-size="IconSize.S"
          :icon-args="{ class: 'text-vlt' }"
          :size="ButtonSize.CHIP"
          a11y-label="More options"
          borderless
          :is-contrast="isContrast"
          @click.prevent />
      </Tooltip>
    </DropdownMenu>

    <!-- Recording: show cancel, meter, finish -->
    <template v-else-if="working">
      <Tooltip :command-id="CommandId.EDITOR_CANCEL_DICTATION">
        <Button
          :btn-style="ButtonStyle.SECONDARY"
          :icon="XIcon"
          :size="ButtonSize.SMALL"
          :icon-args="{ class: 'text-vlt' }"
          class="!p-0.5"
          a11y-label="Cancel dictation"
          :is-contrast="isContrast"
          @click="cancelRecording" />
      </Tooltip>

      <div class="flex h-[26px] w-14 items-center justify-center overflow-hidden text-vlt" aria-hidden="true">
        <AVMedia
          v-if="stream && isRecording && !isTranscribing"
          :media="stream"
          type="frequ"
          :canv-width="56"
          :canv-height="26"
          :line-color="visualizerLineColor"
          :fft-size="512" />
        <LoadingSpinner v-else x-small immediate />
      </div>

      <Tooltip :command-id="CommandId.EDITOR_CONFIRM_DICTATION">
        <Button
          :btn-style="ButtonStyle.RECOMMENDATION"
          :icon="CheckIcon"
          :size="ButtonSize.SMALL"
          a11y-label="Insert the dictated text at the cursor"
          class="!p-0.5"
          :disabled="isStarting || isTranscribing"
          @click="finishRecording" />
      </Tooltip>
    </template>
  </div>
</template>
